/**
 * `ok: true` is not the same claim as "it happened".
 *
 * Every kernel route that can answer "nothing changed" sets `noop: true` beside the data. The
 * producers, enumerated: metadata deploy (twice, `metadata-routes.ts`), package install AND
 * uninstall (two in `packages-routes.ts`), install-by-key, publish (`already_promoted`), and
 * value-set deploy (`value-sets-routes.ts:168`, which is the one returned inline on a `return {`
 * line and therefore the one a line-anchored search misses).
 *
 * DELIBERATELY NOT STATED AS A COUNT, and that decision is inherited rather than re-made. Two
 * successive attempts to put a number on that list got it wrong — first by anchoring the search to
 * `^\s*noop[,:]`, which cannot see the value-set one because it is returned inline on a `return {`
 * line; then by counting modules as though each held one route, when `metadata-routes.ts` and
 * `packages-routes.ts` hold two apiece. A cardinal in a comment is a second claim that has to be
 * maintained beside the list, and it is the half that rots: the list stays checkable by reading,
 * the number silently stops matching the next route anyone adds. Enumerate, search unanchored, and
 * let the list be the count.
 *
 * ── WHY THIS IS ITS OWN MODULE (CAOS-1715) ────────────────────────────────────────────────────
 *
 * CAOS-1712 fixed the REMOVAL direction and put this reading in `surfaces/roster-actions.ts`,
 * where it was the only caller. CAOS-1715 is the INSTALL direction, on a different surface — the
 * Get More Apps / empty-workspace picker — and needed the same reading.
 *
 * Copying it would have produced two implementations of one convention, which is the shape of
 * defect this whole pair of tickets exists to remove: the kernel means one thing by the flag, and
 * two readers of it drift. So it moved here and both surfaces import it. The alternative — a
 * handler that knows about packages, or about removals — has to be written again for every verb,
 * and the next route that no-ops is then covered by neither.
 *
 * It is not in `kernel-contract.ts` even though it is part of the kernel's wire contract: that file
 * says in its own header that it supplies TYPES only, being a hermetic mirror of the kernel's
 * runtime types, and a runtime function does not belong in it.
 */
export function changedNothing(data: unknown): boolean {
  return typeof data === 'object' && data !== null && (data as { noop?: unknown }).noop === true;
}
